'use client';

import React from 'react';
import { hexToRgb } from 'src/utils/color/hexToRgb';
import { faCircle, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';

interface StatusButtonProps {
  variant: 'compact' | 'regular';
  status: { label: string; color: string };
  loading: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  testIdPrefix?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog' | boolean;
  'aria-controls'?: string;
}

/**
 * @description StatusButton component
 * @param variant compact or regular
 * @param status status object
 * @param loading loading state
 * @param onClick click handler
 * @returns React.Node
 */

const StatusButton = ({
  variant,
  status,
  loading,
  onClick,
  testIdPrefix = '',
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHasPopup,
  'aria-controls': ariaControls,
}: StatusButtonProps) => {
  const noStatusColor = '#a7a7a7';
  const backgroundColor = status?.color
    ? hexToRgb(status?.color, 0.1)
    : hexToRgb(noStatusColor, 0.1);
  const backgroundHoverColor = status?.color
    ? hexToRgb(status?.color, 0.2)
    : hexToRgb(noStatusColor, 0.2);
  const btnColor = status?.color || noStatusColor;

  const isRegular = variant === 'regular';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center justify-between gap-1 px-2 py-1 text-sm font-semibold rounded-md rounded-br-3xl transition-colors whitespace-nowrap w-[8.75rem] min-w-[8.75rem] max-w-[8.75rem] h-8 min-h-8 max-h-8 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        backgroundColor,
        color: btnColor,
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.currentTarget.style.backgroundColor = backgroundHoverColor;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = backgroundColor;
      }}
      data-testid={
        testIdPrefix
          ? `${testIdPrefix}-medrm-status-button`
          : 'medrm-status-button'
      }
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-controls={ariaControls}
      aria-label={`Change status, current status: ${status.label}`}
    >
      <span className="flex items-center gap-1.5 overflow-hidden">
        {loading ? (
          <Icon icon={faSpinner} className="animate-spin text-sm" />
        ) : (
          <Icon icon={faCircle} className="text-sm" style={{ color: btnColor }} />
        )}
        {isRegular && (
          <span className="truncate leading-5">{status.label}</span>
        )}
      </span>
      <Icon icon={faChevronDown} className="text-xs flex-shrink-0" />
    </button>
  );
};

export default StatusButton;
